import Box from "@mui/joy/Box";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import Input from "@mui/joy/Input/Input";
import {
  Container,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import RatePanel from "../../components/Home/RatePanel";
import Blocks from "../../components/Home/Blocks";
import Transactions from "../../components/Home/Transactions";
import { useUpcxChain } from "../../hook/useUpcxChain";
import { useEffect, useRef, useState } from "react";
import { GetInfoResult } from "upcx-js/dist/upcxjs-rpc-interfaces";
import { Block, Transaction } from "../../openApi/api";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { ThemeLight } from "../../state/ThemeLight";
import {
  AccountsService,
  BlocksService,
  ContractService,
  TokensService,
  TransactionsService,
} from "../../backend";
import { AnnounceModal } from "../../components/AnnounceModal";

const Home = () => {
  const { jsonRpc, selectedShard } = useUpcxChain();
  const navigate = useNavigate();

  const [themeLight] = useRecoilState(ThemeLight);

  const [chainInfo, setChainInfo] = useState<GetInfoResult>();
  const [prevBlock, setPrevBlock] = useState(0);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [txs, setTxs] = useState<Transaction[]>([]);
  const [txCount, setTxCount] = useState<number>();

  const [searchOption, setSearchOption] = useState("0");
  const [search, setSearch] = useState("");

  const blockIntervalHandler = useRef<any>();
  const txIntervalHandler = useRef<any>();

  const initialize = async () => {
    if (!jsonRpc) return;
    const result = await jsonRpc.get_info();
    setChainInfo(result);
  };

  const mergeBlock = (newBlocks: Block[], prevBlock: Block[]) => {
    return [...newBlocks, ...prevBlock.slice(0, 11 - newBlocks.length)];
  };

  const getBlockList = async () => {
    if (!chainInfo || chainInfo.head_block_num === prevBlock) {
      return;
    }

    const result = await BlocksService.getBlocks(
      Math.min(11, chainInfo.head_block_num - prevBlock),
      0
    );
    setBlocks(mergeBlock(result.blocks ?? [], blocks));
    setPrevBlock(chainInfo.head_block_num);
  };

  const getTransactionList = async () => {
    const result = await TransactionsService.getTransactions(undefined, 10, 0);
    setTxs(result.txs ?? []);
    setTxCount(result.total);
  };

  useEffect(() => {
    if (jsonRpc && selectedShard) {
      setBlocks([]);
      setPrevBlock(0);

      if (blockIntervalHandler.current) {
        clearInterval(blockIntervalHandler.current);
      }

      const handler = setInterval(initialize, 2000);
      blockIntervalHandler.current = handler;
    }

    // eslint-disable-next-line
  }, [jsonRpc, selectedShard]);

  useEffect(() => {
    getBlockList();
    // eslint-disable-next-line
  }, [chainInfo, prevBlock, blocks]);

  useEffect(() => {
    if (selectedShard) {
      if (txIntervalHandler.current) {
        clearInterval(txIntervalHandler.current);
      }

      const handler = setInterval(() => getTransactionList(), 2000);
      txIntervalHandler.current = handler;

      setBlocks([]);
      setPrevBlock(0);
    }
  }, [selectedShard]);

  const onSearch = async () => {
    if (!search) return;

    if (search === "upcx") {
      if (searchOption === "0" || searchOption === "1") {
        const result = await AccountsService.getAccountByName(search);
        if (result) {
          navigate(`/address/${search}`);
          return;
        }
      }

      if (searchOption === "0" || searchOption === "4") {
        const contract = await ContractService.getContract(search);
        if (contract) {
          navigate(`/contract/${contract.name}`);
          return;
        }
      }
    } else {
      if (searchOption === "0" || searchOption === "4") {
        const contract = await ContractService.getContract(search);
        if (contract) {
          navigate(`/contract/${contract.name}`);
          return;
        }
      }

      if (searchOption === "0" || searchOption === "1") {
        const result = await AccountsService.getAccountByName(search);
        if (result) {
          navigate(`/address/${search}`);
          return;
        }
      }
    }

    if (searchOption === "0" || searchOption === "2") {
      const tokenResult = await TokensService.getTokens(5, 0, search);
      if (tokenResult.total === 1) {
        navigate(`/token/${search}`);
        return;
      }
    }

    if (searchOption === "0" || searchOption === "3") {
      const ifBlockValue = parseInt(search);
      if (
        !isNaN(ifBlockValue) &&
        chainInfo &&
        ifBlockValue <= chainInfo.head_block_num
      ) {
        navigate(`/block/${ifBlockValue}`);
        return;
      }
    }

    if (searchOption === "0") {
      try {
        const txResult = await TransactionsService.getTransactionsById(search);
        navigate(`/tx/${txResult.txId}?blockNum=${txResult.blockNum}`);
        return;
      } catch {}
    }

    navigate("/notfound");
  };

  return (
    <>
      <section className="pt-14 pb-20 home-bg">
        <Container className="container-xxl">
          <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
            <Box gridColumn="span 2"></Box>
            <Box gridColumn="span 8">
              <Typography
                className="fs-lg text-white fw-bold mb-3"
                variant="h6"
              >
                Explorer UPCX Blockchain
              </Typography>

              <div className="search-panel-container border rounded d-flex gap-2 p-1.5">
                <Select
                  placeholder="All filters"
                  // indicator={<KeyboardArrowDown />}
                  sx={{
                    width: 160,
                    height: 36,
                    border: "unset",
                    boxShadow: "unset",
                    backgroundColor: themeLight ? "white" : "#090909",
                  }}
                  value={searchOption}
                  onChange={(e) => setSearchOption(e.target.value)}
                >
                  <MenuItem value="0">All Filters</MenuItem>
                  <MenuItem value="1">Addresses</MenuItem>
                  <MenuItem value="2">Tokens</MenuItem>
                  <MenuItem value="3">Blocks</MenuItem>
                  <MenuItem value="4">Contracts</MenuItem>
                </Select>
                <div className="flex-grow-1 position-relative auto-search-max-height">
                  <TextField
                    className="form-control fs-base border-0"
                    placeholder="Search by Account / Txn Hash / Block / Token"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onSearch();
                      }
                    }}
                    variant="outlined"
                    sx={{
                      ".MuiOutlinedInput-root": {
                        height: "36px",
                      },
                      ".MuiOutlinedInput-input": {
                        paddingTop: "20px",
                      },
                      height: "36px",
                    }}
                  />
                </div>
                <div>
                  <button
                    className="btn fs-base btn-primary"
                    onClick={onSearch}
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </Box>
          </Box>
        </Container>
      </section>

      <Container className="container-xxl">
        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={7}>
          <Box gridColumn="span 12">
            <RatePanel chainInfo={chainInfo} txCount={txCount} />
          </Box>
          <Box gridColumn="span 6">
            <Blocks blocks={blocks} />
          </Box>
          <Box gridColumn="span 6">
            <Transactions txs={txs} />
          </Box>
        </Box>
      </Container>
      {/* <AnnounceModal /> */}
    </>
  );
};

export default Home;
